"use client";

import PropTypes from "prop-types";
import requiredForA11y from "prop-types-extra/lib/isRequiredForA11y";
import Nav from "./Nav";
import NavItem from "./NavItem";
import TabContainer from "./TabContainer";
import TabContainerContext from "./TabContainerContext";
import TabContent from "./TabContent";
import ValidComponentChildren from "./utils/ValidComponentChildren";
import { bsClass as setBsClass } from "./utils/bootstrapUtils";

const propTypes = {
  /**
   * Mark the Tab with a matching `eventKey` as active.
   *
   * @controllable onSelect
   */
  activeKey: PropTypes.any,

  /**
   * Navigation style
   */
  bsStyle: PropTypes.oneOf(["tabs", "pills"]),

  animation: PropTypes.bool,

  id: requiredForA11y(PropTypes.oneOfType([
    PropTypes.string, PropTypes.number,
  ])),

  /**
   * Callback fired when a Tab is selected.
   *
   * ```js
   * function (
   *   Any eventKey,
   *   SyntheticEvent event?
   * )
   * ```
   *
   * @controllable activeKey
   */
  onSelect: PropTypes.func,

  /**
   * Wait until the first "enter" transition to mount tabs (add them to the DOM)
   */
  mountOnEnter: PropTypes.bool,

  /**
   * Unmount tabs (remove it from the DOM) when it is no longer visible
   */
  unmountOnExit: PropTypes.bool,
};

const defaultProps = {
  bsStyle: "tabs",
  animation: true,
  mountOnEnter: false,
  unmountOnExit: false,
};

function getDefaultActiveKey(children: any) {
  let defaultActiveKey: any;

  ValidComponentChildren.forEach(children, (child: any) => {
    if (defaultActiveKey == null) {
      defaultActiveKey = child.props.eventKey;
    }
  });

  return defaultActiveKey;
}

function Tabs(props) {
  const renderTab = (child: any) => {
    const {
      title, eventKey, disabled, tabClassName, href,
    } = child.props;
    if (title == null) {
      return null;
    }

    return (

      <NavItem
        eventKey={eventKey}
        disabled={disabled}
        className={tabClassName}
        href={href}
        component={props.component}
      >
        {title}
      </NavItem>
    );
  };

  const {
    id,
    onSelect,
    animation,
    mountOnEnter,
    unmountOnExit,
    bsClass,
    className,
    style,
    children,
    activeKey = getDefaultActiveKey(children),
    ...restProps
  } = props;

  return (
    <TabContainer
      id={id}
      activeKey={activeKey}
      onSelect={onSelect}
      className={className}
      style={style}
    >

      <div>

        <TabContainerContext.Consumer>
          {({ $bs_tabContainer }) => (
            <Nav
              {...restProps}
              role="tablist"
              tabContainerContext={$bs_tabContainer}
            >

              {ValidComponentChildren.map(children, renderTab)}
            </Nav>
          )}

        </TabContainerContext.Consumer>

        <TabContent
          bsClass={bsClass}
          animation={animation}
          mountOnEnter={mountOnEnter}
          unmountOnExit={unmountOnExit}
        >
          {children}
        </TabContent>
      </div>
    </TabContainer>
  );
}

Tabs.propTypes = propTypes;
Tabs.defaultProps = defaultProps;

setBsClass("tab", Tabs);

export default Tabs;
